import { createSlice } from '@reduxjs/toolkit';

import { changeGlobalParameter } from 'shared/product_tabs/tab_project/actions/changeGlobalParameter';
import {
  isParameterNameQtyValid,
  isProductTypeParameterNameQtyValid,
} from 'shared/utils/validateQuantities';
import {
  ERROR,
  PRICE_SELECTED,
  QUOTED,
  QUOTING,
  START,
} from 'shared/data/quoteSteps';

import initialState from 'shared/product_tabs/tab_assembly/data/initialState';
import defaultState from 'shared/product_tabs/tab_assembly/data/defaultState';
import defaultQuote from 'shared/product_tabs/tab_assembly/data/defaultQuote';
import { changeParameterByName } from 'shared/product_tabs/tab_assembly/slice/onChangeParameter';
import { doQuote } from 'shared/product_tabs/tab_assembly/slice/thunks';
import { validator } from 'shared/product_tabs/tab_assembly/slice/validator';
import { shouldForceRequote } from 'shared/utils/shouldForceRequote';
import { doAddToCart } from 'shared/thunks';

const assemblySlice = createSlice({
  name: 'assemblySlice',
  initialState: {
    ...initialState,
    validatorFlag: false,
  },
  reducers: {
    changeParameter(state, action) {
      const { name, value } = action.payload;

      changeParameterByName(state.parameters, name, value);

      state.parametersChanged = shouldForceRequote(name);

      if (isParameterNameQtyValid(name)) {
        if (!state.quantitiesChanged) state.quantitiesChanged = true;

        if (state.quote.line_items.length > 0 && state.quoteStep === START) {
          const qty_index = Number(name.split('qty')[1]) - 1;

          state.quote.line_items.forEach((li) => {
            li.quantities[qty_index] = value;
          });
        }
      }
    },
    deleteAssembly(state) {
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();
      const newState = _.cloneDeep({ ...defaultState, validatorFlag: false });
      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.assemblyVariants = state.assemblyVariants;
      newState.currentVariant = state.currentVariant;
      newState.destroy = true;
      newState.zip_file_input_id = state.zip_file_input_id;
      return newState;
    },
    updateValidatorFlag(state) {
      state.validatorFlag = !state.validatorFlag;
    },
    priceSelected(state, action) {
      const {
        row_selected,
        column_selected,
        supplier,
        currentVariant,
      } = action.payload;

      state.parameters.row_selected = row_selected;
      state.parameters.column_selected = column_selected;
      state.parameters.supplier_selected = supplier;
      state.currentVariant = currentVariant;
      state.quoteStep = PRICE_SELECTED;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload;
        if (['part_number', 'revision'].includes(name)) {
          state.parameters[name] = value;
        } else if (name === 'zip_input_id') {
          state.zip_file_input_id = value;
        } else if (isProductTypeParameterNameQtyValid(name, 'fabrication') &&
          !state.quantitiesChanged && state.quoteStep === START) {
          const qty_index = Number(name.split('_qty')[1]) - 1;
          state.parameters.quantities[qty_index] = value;
          state.quote.line_items[0]['quantities'][qty_index] = value;
        }
      })
      .addCase(doAddToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(doAddToCart.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
        state.quoteStep = QUOTING;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.supplier_selected = '';

        if (!quote || !quote.line_items) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quote = defaultQuote;
        state.quoteStep = ERROR;
      });
  },
});

export const {
  changeParameter,
  priceSelected,
  deleteAssembly,
  updateValidatorFlag,
} = assemblySlice.actions;

export default assemblySlice.reducer;
